import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { FormattedMessage } from 'react-intl';
import Close from '@material-ui/icons/Close';
import MuiAlert from '@material-ui/lab/Alert';
import { Button, IconButton, Snackbar, Typography } from '@material-ui/core';

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

class Errors extends React.Component {
  state = {
    open: false,
    lastMsg: null,
    lastTs: null,
  };

  // Open when a new/changed error arrives; close when cleared.
  static getDerivedStateFromProps(nextProps, prevState) {
    const errs = nextProps.errors || {};
    const hasError = !isEmpty(nextProps.errors);
    const msg = errs.message;
    const ts = errs.timestamp;

    // Cleared -> close
    if (!hasError && prevState.open) {
      return { open: false, lastMsg: null, lastTs: null };
    }

    // New/changed error -> open
    if (hasError && (msg !== prevState.lastMsg || ts !== prevState.lastTs)) {
      return { open: true, lastMsg: msg, lastTs: ts };
    }

    return null; // no state change
  }

  autoClose = () => {
    this.setState({ open: false });
  };

  handleOnClose = () => {
    this.autoClose();
    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  handleOnRetry = () => {
    this.autoClose();
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  handleSnackbarClose = (_event, reason) => {
    const { dismissBehavior } = this.props;
    const blockClickaway = dismissBehavior !== 'auto';
    const blockOthers = dismissBehavior === 'persistent';

    // Only allow auto-close if dismissBehavior is 'auto', or if 'manual' and not clickaway
    if (
      (blockClickaway && reason === 'clickaway') ||
      (blockOthers && (reason === 'escapeKeyDown' || reason === 'timeout'))
    )
      return;
    this.handleOnClose();
  };

  render() {
    const { errors, isPaymentError } = this.props;
    if (isEmpty(errors)) return null;

    const { message } = errors;
    const isNode = React.isValidElement(message);

    let text;
    if (isPaymentError) {
      text = (
        <FormattedMessage
          id="error.failedPayment"
          defaultMessage="Your payment failed and the charge have not been done.
            Your bank returned error: '{message}' Try again and contact the support
            if the error persists."
          values={{ message }}
        />
      );
    } else if (isNode) {
      // message is already a React node
      text = message;
    } else if (typeof message === 'string') {
      // i18n id: render with fallback to the same string
      text = <FormattedMessage id={message} defaultMessage={message} />;
    } else if (message && typeof message.id === 'string') {
      // (optional) support { id, defaultMessage } objects
      text = (
        <FormattedMessage
          id={message.id}
          defaultMessage={message.defaultMessage || message.id}
          values={message.values}
        />
      );
    } else {
      // final fallback
      text = (
        <FormattedMessage
          id="error.somethingWrong"
          defaultMessage="Something went wrong, please try again!"
        />
      );
    }

    const wrongPwd = message && message === 'Your current password is incorrect';

    return (
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={this.state.open}
        onClose={this.handleSnackbarClose}
        autoHideDuration={this.props.dismissBehavior === 'auto' ? this.props.autoHideMs : null}
      >
        <Alert
          onClose={this.props.dismissBehavior === 'persistent' ? undefined : this.autoClose}
          severity="error"
          sx={{
            alignItems: 'center', // vertical align icon+text
            '& .MuiAlert-action': { paddingTop: 0 }, // Remove default padding
          }}
          action={
            <>
              {errors.canTryAgain && !!this.props.onRetry && (
                <Button
                  color="inherit"
                  variant="outlined"
                  onClick={this.handleOnRetry}
                  size="small"
                >
                  {errors.tryAgainLabel || (
                    <FormattedMessage id="ui.tryAgain" defaultMessage="Try Again" />
                  )}
                </Button>
              )}
              {this.props.dismissBehavior !== 'persistent' && (
                <IconButton
                  aria-label="Close"
                  color="inherit"
                  onClick={this.handleOnClose}
                  size="small"
                >
                  <Close fontSize="small" />
                </IconButton>
              )}
            </>
          }
        >
          {wrongPwd && (
            <Typography>
              <FormattedMessage
                id="Your current password is incorrect"
                defaultMessage="Your current password is incorrect"
              />
            </Typography>
          )}
          {(errors.status === 400 || errors.status === 404) && !wrongPwd && (
            <Typography>
              {/* `text`, not `message`: the api sends English sentences that are
                  looked up as translation ids above. Rendering the raw message
                  here left every 400 untranslated, which is why the one string
                  anyone noticed -- the password one -- carries a hardcoded
                  special case instead of being handled here. */}
              {message ? (
                text
              ) : (
                <FormattedMessage
                  id="error.badUrl"
                  defaultMessage="Bad URL. Please check the URL."
                />
              )}
            </Typography>
          )}
          {errors.status !== 400 && errors.status !== 404 && (
            <Typography>
              {text || (
                <FormattedMessage
                  id="error.somethingWrong"
                  defaultMessage="Something went wrong, please try again!"
                />
              )}
            </Typography>
          )}
        </Alert>
      </Snackbar>
    );
  }
}

Errors.propTypes = {
  errors: PropTypes.shape({
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.object]),
    status: PropTypes.number,
    timestamp: PropTypes.number,
    canTryAgain: PropTypes.bool,
    tryAgainLabel: PropTypes.node,
    code: PropTypes.string, // e.g. 'poll_timeout'
  }),
  isPaymentError: PropTypes.bool,
  dismissBehavior: PropTypes.oneOf(['auto', 'manual', 'persistent']),
  autoHideMs: PropTypes.number, // only for 'auto' dismissBehavior
  onClose: PropTypes.func, // close (X)
  onRetry: PropTypes.func, // “Try Again”
};

Errors.defaultProps = {
  errors: null,
  isPaymentError: false,
  dismissBehavior: 'auto',
  autoHideMs: null,
  onClose: null,
  onRetry: null,
};

export default Errors;
